/**
 * Tab rendering functions for Student Profile (Personal, Contact, Academic, Other)
 */
import React from 'react';
import { Divider } from 'primereact/divider';
import { Badge } from 'primereact/badge';

/**
 * Render Personal Tab
 * @param {Object} studentData - Student data object
 * @returns {JSX.Element}
 */
export const renderPersonalTab = (studentData) => {
    return (
        <div className="student-profile-content">
            <div className="info-section">
                <div className="section-header">
                    <i className="pi pi-user"></i>
                    <h3>Personal Information</h3>
                </div>
                <div className="info-grid">
                    <div className="info-item">
                        <label>First Name</label>
                        <p>{studentData?.firstName || '—'}</p>
                    </div>
                    <div className="info-item">
                        <label>Middle Name</label>
                        <p>{studentData?.middleName || '—'}</p>
                    </div>
                    <div className="info-item">
                        <label>Last Name</label>
                        <p>{studentData?.lastName || '—'}</p>
                    </div>
                </div>

                <div className="info-grid">
                    <div className="info-item">
                        <label>Gender</label>
                        <p>{studentData?.gender || '—'}</p>
                    </div>
                    <div className="info-item">
                        <label>Date of Birth</label>
                        <div className="date-display">
                            <i className="pi pi-calendar" style={{ color: '#076EFF', marginRight: '8px' }}></i>
                            <span style={{ color: '#076EFF' }}>
                                {studentData?.dateOfBirth
                                    ? studentData.dateOfBirth.toLocaleDateString('en-GB', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric'
                                    })
                                    : '—'
                                }
                            </span>
                        </div>
                    </div>
                </div>

                <div className="info-grid">
                    <div className="info-item">
                        <label>Category</label>
                        <p>{studentData?.category || '—'}</p>
                    </div>
                    <div className="info-item">
                        <label>Ref/Adm No</label>
                        <p>{studentData?.refAdmNo || '—'}</p>
                    </div>
                    <div className="info-item">
                        <label>Appl. No</label>
                        <p>{studentData?.apptNo || '—'}</p>
                    </div>
                </div>
            </div>

            <Divider />

            <div className="info-section">
                <div className="section-header">
                    <i className="pi pi-calendar"></i>
                    <h3>Registration Details</h3>
                </div>
                <div className="info-grid">
                    <div className="info-item">
                        <label>Admission Date</label>
                        <div className="date-display">
                            <i className="pi pi-calendar" style={{ color: '#076EFF', marginRight: '8px' }}></i>
                            <span style={{ color: '#076EFF' }}>
                                {studentData?.admissionDate
                                    ? studentData.admissionDate.toLocaleDateString('en-GB', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric'
                                    })
                                    : '—'
                                }
                            </span>
                        </div>
                    </div>
                    <div className="info-item">
                        <label>Registration Date</label>
                        <div className="date-display">
                            <i className="pi pi-calendar" style={{ color: '#076EFF', marginRight: '8px' }}></i>
                            <span style={{ color: '#076EFF' }}>
                                {studentData?.registrationDate
                                    ? studentData.registrationDate.toLocaleDateString('en-GB', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric'
                                    })
                                    : '—'
                                }
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

/**
 * Render Contact Tab
 * @param {Object} studentData - Student data object
 * @returns {JSX.Element}
 */
export const renderContactTab = (studentData) => {
    return (
        <div className="student-profile-content">
            <div className="info-section">
                <div className="section-header">
                    <i className="pi pi-phone"></i>
                    <h3>Student Contact</h3>
                </div>
                <div className="info-grid">
                    <div className="info-item">
                        <label>Point of Contact</label>
                        <p>{studentData?.pointOfContact || '—'}</p>
                    </div>
                    <div className="info-item">
                        <label>Student Mobile</label>
                        <p>{studentData?.studentMobile || '—'}</p>
                    </div>
                </div>
            </div>

            <Divider />

            <div className="info-section">
                <div className="section-header">
                    <i className="pi pi-users"></i>
                    <h3>Father Details</h3>
                </div>
                <div className="info-grid">
                    <div className="info-item">
                        <label>Father Name</label>
                        <p>{studentData?.fatherName || '—'}</p>
                    </div>
                    <div className="info-item">
                        <label>Father Mobile</label>
                        <p>{studentData?.fatherMobile || '—'}</p>
                    </div>
                    <div className="info-item">
                        <label>Father Email ID</label>
                        <p>{studentData?.fatherEmail || '—'}</p>
                    </div>
                </div>
            </div>

            <Divider />

            <div className="info-section">
                <div className="section-header">
                    <i className="pi pi-users"></i>
                    <h3>Mother Details</h3>
                </div>
                <div className="info-grid">
                    <div className="info-item">
                        <label>Mother Name</label>
                        <p>{studentData?.motherName || '—'}</p>
                    </div>
                    <div className="info-item">
                        <label>Mother Mobile</label>
                        <p>{studentData?.motherMobile || '—'}</p>
                    </div>
                    <div className="info-item">
                        <label>Mother Email ID</label>
                        <p>{studentData?.motherEmail || '—'}</p>
                    </div>
                </div>
            </div>

            {/* Guardian Details - Only show if guardian data exists */}
            {(studentData?.guardianName || studentData?.guardianMobile || studentData?.guardianEmail) && (
                <>
                    <Divider />
                    <div className="info-section">
                        <div className="section-header">
                            <i className="pi pi-user"></i>
                            <h3>Guardian Details</h3>
                        </div>
                        <div className="info-grid">
                            <div className="info-item">
                                <label>Guardian Name</label>
                                <p>{studentData?.guardianName || '—'}</p>
                            </div>
                            <div className="info-item">
                                <label>Guardian Mobile</label>
                                <p>{studentData?.guardianMobile || '—'}</p>
                            </div>
                            <div className="info-item">
                                <label>Guardian Email ID</label>
                                <p>{studentData?.guardianEmail || '—'}</p>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

/**
 * Render Academic Tab
 * @param {Object} studentData - Student data object
 * @returns {JSX.Element}
 */
export const renderAcademicTab = (studentData) => {
    return (
        <div className="student-profile-content">
            <div className="info-section">
                <div className="section-header">
                    <i className="pi pi-users"></i>
                    <h3>Student Groups</h3>
                </div>
                <div className="groups-container">
                    {(studentData?.studentGroups && studentData.studentGroups.length > 0) ? (
                        studentData.studentGroups.map((group, index) => (
                            <Badge key={index} value={group} className="group-badge"></Badge>
                        ))
                    ) : (
                        <p>No groups assigned</p>
                    )}
                </div>
            </div>
        </div>
    );
}

/**
 * Render Other Tab
 * @param {Object} studentData - Student data object
 * @returns {JSX.Element}
 */
export const renderOtherTab = (studentData) => {
    return (
        <div className="student-profile-content">
            <div className="info-section">
                <div className="section-header">
                    <i className="fa fa-bus"></i>
                    <h3>Transport Information</h3>
                </div>
                <div className="info-grid">
                    <div className="info-item">
                        <label>Transport Avail</label>
                        <p>{studentData?.transportAvail || '—'}</p>
                    </div>
                    <div className="info-item">
                        <label>Transport ID</label>
                        <p>{studentData?.transportID || '—'}</p>
                    </div>
                    <div className="info-item">
                        <label>Transport RFID</label>
                        <p>{studentData?.transportRFID || '—'}</p>
                    </div>
                </div>

                <div className="info-grid">
                    <div className="info-item">
                        <label>Staff Kid</label>
                        <p>{studentData?.staffKid || '—'}</p>
                    </div>
                    <div className="info-item">
                        <label>Siblings</label>
                        <p>{studentData?.siblings && studentData.siblings.length > 0 ? 'Yes' : 'No'}</p>
                    </div>
                    <div className="info-item">
                        <label>Biometric Code</label>
                        <p>{studentData?.biometricCode || '—'}</p>
                    </div>
                </div>

                <div className="info-grid">
                    <div className="info-item full-width">
                        <label>Lunch Fee</label>
                        <p>{studentData?.lunchFee || '—'}</p>
                    </div>
                </div>
            </div>

            <Divider />

            <div className="info-section">
                <div className="section-header">
                    <i className="pi pi-map-marker"></i>
                    <h3>Address & Location</h3>
                </div>
                <div className="info-grid">
                    <div className="info-item full-width">
                        <label>Current Address</label>
                        <p>
                            {[
                                studentData?.addressLine,
                                studentData?.cityOrDist,
                                studentData?.state,
                                studentData?.pinCode
                            ].filter(Boolean).join(', ') || '—'}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
